import {
  CompositeFilterDescriptor,
  DataResult,
  FilterOperator,
  State,
} from '@progress/kendo-data-query';
import { produce } from 'immer';
import _ from 'lodash';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import {
  GridCustomCellProps,
  GridDataStateChangeEvent,
} from '@progress/kendo-react-grid';
import { InputChangeEvent } from '@progress/kendo-react-inputs';
import {
  showErrorToast,
  showSuccessToast,
} from '../../../../../../common/global/helpers';
import useBreakpoints from '../../../../../../common/hooks/useBreakpoints';
import useMount from '../../../../../../common/hooks/useMount';
import { DataLoader } from '../../../../../../common/widgets/DataLoader';
import Dropdown from '../../../../../../common/widgets/Dropdown';
import ExpandablePanel from '../../../../../../common/widgets/ExpandablePanel';
import Grid from '../../../../../../common/widgets/Grid';
import Icons from '../../../../../../common/widgets/Icons';
import Input from '../../../../../../common/widgets/Input';
import Loader from '../../../../../../common/widgets/Loader';
import LoadingSpinner from '../../../../../../common/widgets/LoadingSpinner';
import { config } from '../../../../config';
import { CustomerPermissionsContext } from '../../../../contexts/CustomerPermissionsContext';
import { EmployeeItemModel } from '../../../../models/EmployeeItemModel';
import { resources } from '../../../../resources/resources';
import { companyService } from '../../../../services/company';
import { employeeAdminService } from '../../../../services/employee-admin';
import { ListItem } from '../../types';
import {
  EmployeeStateToBoolean,
  SearchCrtIdToFieldName,
  SearchFieldNames,
} from '../helper';
import {
  ResendEmail,
  StateCell,
  companyNameCell,
  emailCell,
  employeeNameCell,
} from './Components/CustomCells';

export interface EmployeeAdminState {
  employees: {
    data: EmployeeItemModel[];
    dataState: State;
    total: number;
  };
  bypassRequestDataIfNeeded: boolean;
}
const initialDataState: State = {
  skip: 0,
  take: config.grid.defaultPageSize,
  sort: [{ field: 'fullName', dir: 'asc' }],
};
export default function EmployeesAdminPage() {
  const { t: translate } = useTranslation();
  const navigate = useNavigate();
  const permissions = useContext(CustomerPermissionsContext);
  const { isMobile } = useBreakpoints();

  const canImportFiles =
    permissions.canImportEmployees || permissions.canImportBudget;
  const companyDefaultItem: ListItem = {
    id: 0,
    name: translate(resources.admin.employees.filters.companyDefault),
  };
  const searchCrtItems: ListItem[] = [
    {
      id: 0,
      name: translate(resources.admin.employees.filters.searchDefault),
    },
    {
      id: 1,
      name: translate(resources.admin.employees.details.general.lastName),
    },
    {
      id: 2,
      name: translate(resources.admin.employees.grid.columns.email),
    },
    {
      id: 3,
      name: translate(resources.admin.employees.details.general.cnp),
    },
    {
      id: 4,
      name: translate(resources.admin.employees.details.general.eid),
    },
    {
      id: 5,
      name: translate(resources.admin.employees.grid.columns.company),
    },
  ];
  const stateItems: ListItem[] = [
    {
      id: 0,
      name: translate(resources.admin.employees.filters.stateDefault),
    },
    {
      id: 1,
      name: translate(resources.admin.employees.filters.stateActive),
    },
    {
      id: 2,
      name: translate(resources.admin.employees.filters.stateDeactivated),
    },
  ];

  const [state, setState] = useState<EmployeeAdminState>({
    employees: { data: [] as EmployeeItemModel[], dataState: initialDataState },
  } as EmployeeAdminState);
  const [companyItems, setCompanyItems] = useState<ListItem[]>([]);
  const [companyFilter, setCompanyFilter] =
    useState<ListItem>(companyDefaultItem);
  const [stateFilter, setStateFilter] = useState<ListItem>(stateItems[0]);
  const [searchFilter, setSearchFilter] = useState<string>('');
  const [searchCrtFilter, setSearchCrtFilter] = useState<ListItem>(
    searchCrtItems[0],
  );

  const getFilter = useCallback((): CompositeFilterDescriptor => {
    const filter: CompositeFilterDescriptor = { logic: 'and', filters: [] };
    if (companyFilter.id !== 0)
      filter.filters.push({
        field: 'companyId',
        operator: FilterOperator.EqualTo,
        value: companyFilter.id,
      });

    if (stateFilter.id !== 0)
      filter.filters.push({
        field: 'isActive',
        operator: FilterOperator.EqualTo,
        value: EmployeeStateToBoolean[stateFilter.id],
      });

    if (!_.isEmpty(searchFilter)) {
      if (searchCrtFilter.id === 0) {
        const filters = SearchFieldNames.map((s: string) => {
          return {
            field: s,
            operator: FilterOperator.Contains,
            value: searchFilter.toLowerCase(),
            ignoreCase: true,
          };
        });
        filter.filters.push({ logic: 'or', filters });
      } else {
        filter.filters.push({
          field: SearchCrtIdToFieldName[searchCrtFilter.id],
          operator: FilterOperator.Contains,
          value: searchFilter.toLowerCase(),
          ignoreCase: true,
        });
      }
    }
    return filter;
  }, [companyFilter.id, searchCrtFilter.id, searchFilter, stateFilter.id]);

  const setFilter = useCallback(() => {
    const filter = getFilter();
    setState(
      produce(d => {
        d.employees.dataState.filter = filter;
      }),
    );
  }, [getFilter]);

  const onCampanyFilterChanged = (e: DropDownListChangeEvent) => {
    const company = e.value as ListItem;
    setCompanyFilter(company);
  };

  const onSearchCrtFilterChanged = (e: DropDownListChangeEvent) => {
    const searchCrt = e.value as ListItem;
    setSearchCrtFilter(searchCrt);
  };

  const onSearchFilterChange = (e: InputChangeEvent) => {
    const search = e.value;
    setSearchFilter(search);
  };

  const onStateFilterChanged = (e: DropDownListChangeEvent) => {
    const state = e.value as ListItem;
    setStateFilter(state);
  };

  const onDataReceived = (result: DataResult) => {
    setState(
      produce(draft => {
        draft.employees.data = result.data.map((e: EmployeeItemModel) => {
          return { ...e, isApproving: false };
        });
        draft.employees.total = result.total;
        draft.bypassRequestDataIfNeeded = false;
      }),
    );
  };

  const onDataStateChange = (e: GridDataStateChangeEvent) => {
    setState(
      produce(draft => {
        draft.employees.dataState = e.dataState;
      }),
    );
  };

  const handleResendEmail = async (
    employeeId: number,
    employeeName: string,
  ) => {
    const currentEmployeeIndex =
      state.employees.data.findIndex(e => e.employeeId === employeeId) ?? -1;
    setState(
      produce(d => {
        if (currentEmployeeIndex >= 0)
          d.employees.data[currentEmployeeIndex].isApproving = true;
      }),
    );
    const response = await employeeAdminService.approveAccount(employeeId);
    if (!response.hasErrors) {
      setState(
        produce(d => {
          d.bypassRequestDataIfNeeded = true;
          if (currentEmployeeIndex >= 0)
            d.employees.data[currentEmployeeIndex].isApproving = false;
        }),
      );
      showSuccessToast(
        translate(resources.admin.employees.resendEmailSuccess, {
          0: employeeName,
        }),
      );
    } else {
      setState(
        produce(d => {
          if (currentEmployeeIndex >= 0)
            d.employees.data[currentEmployeeIndex].isApproving = false;
        }),
      );
      showErrorToast(
        translate(resources.admin.employees.resendEmailError, {
          0: employeeName,
        }),
      );
    }
  };

  const loadOtherData = (abortController: AbortController | undefined) => {
    (async () => {
      const result = await companyService.getCompaniesByClient(
        '?$select=CompanyId, Name',
        {
          signal: abortController?.signal,
        },
      );

      if (!result.hasErrors) {
        setCompanyItems(
          result.data.map(c => {
            return { id: c.companyId, name: c.name };
          }) as ListItem[],
        );
      }
    })();
  };
  const ResendEmailCustomCell = (props: GridCustomCellProps) => (
    <ResendEmail {...props} handleResendEmail={handleResendEmail} />
  );

  useEffect(() => {
    setFilter();
  }, [companyFilter, stateFilter, searchCrtFilter, searchFilter, setFilter]);

  useMount(abortController => {
    loadOtherData(abortController);
  });

  return (
    <div className="panel padding-xl employee-panel">
      <div className="page-actions">
        <h1>{translate(resources.admin.employees.title)}</h1>
        <div className="page-action admin-actions">
          <button
            className="btn btn-solid btn-sm"
            onClick={() => navigate(`add-employee`)}
          >
            <span className="btn-icon">
              <Icons.User />
            </span>
            <span className="btn-text">
              {translate(resources.admin.employees.actions.addEmployee)}
            </span>
          </button>
          {canImportFiles && (
            <button
              className="btn btn-solid btn-sm"
              onClick={() => navigate('import-files')}
            >
              <span className="btn-icon">
                <Icons.Report />
              </span>
              <span className="btn-text">
                {translate(resources.admin.employees.actions.loadFile)}
              </span>
            </button>
          )}
        </div>
      </div>

      {/* Employees search*/}
      {!isMobile ? (
        <div className="employee-search">
          <div>
            <div>
              <div className="employee-search-column esi-c">
                <Dropdown
                  value={companyFilter}
                  defaultValue={companyDefaultItem}
                  data={_.orderBy(
                    [...companyItems, companyDefaultItem],
                    'id',
                    'asc',
                  )}
                  dataItemKey="id"
                  textField="name"
                  onChange={onCampanyFilterChanged}
                />
              </div>
              <div className="employee-search-column esi-e">
                <Dropdown
                  value={searchCrtFilter}
                  defaultValue={searchCrtItems[0]}
                  data={searchCrtItems}
                  dataItemKey="id"
                  textField="name"
                  onChange={onSearchCrtFilterChanged}
                />
                <Input
                  type="text"
                  value={searchFilter}
                  placeholder={translate(
                    resources.admin.employees.filters.searchPlaceholder,
                  )}
                  onChange={onSearchFilterChange}
                />
              </div>

              <div className="employee-search-column esi-s">
                <Dropdown
                  value={stateFilter}
                  defaultValue={stateItems[0]}
                  data={stateItems}
                  dataItemKey="id"
                  textField="name"
                  onChange={onStateFilterChanged}
                />
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="employee-search-mobile">
          <div className="esi-e">
            <Dropdown
              value={searchCrtFilter}
              defaultValue={searchCrtItems[0]}
              data={searchCrtItems}
              dataItemKey="id"
              textField="name"
              onChange={onSearchCrtFilterChanged}
            />
            <Input
              type="text"
              value={searchFilter}
              placeholder={translate(
                resources.admin.employees.filters.searchPlaceholder,
              )}
              onChange={onSearchFilterChange}
            />
          </div>
          <ExpandablePanel
            expandOn={'header'}
            arrow={false}
            title={<div>{translate(resources.admin.advancedSearchLabel)}</div>}
          >
            <div className="admin-employees-adv-search">
              <div className="esi-c">
                <Dropdown
                  value={companyFilter}
                  defaultValue={companyDefaultItem}
                  data={_.orderBy(
                    [...companyItems, companyDefaultItem],
                    'id',
                    'asc',
                  )}
                  dataItemKey="id"
                  textField="name"
                  onChange={onCampanyFilterChanged}
                />
              </div>
              <div className="esi-s">
                <Dropdown
                  value={stateFilter}
                  defaultValue={stateItems[0]}
                  data={stateItems}
                  dataItemKey="id"
                  textField="name"
                  onChange={onStateFilterChanged}
                />
              </div>
            </div>
          </ExpandablePanel>
        </div>
      )}

      <DataLoader
        getData={employeeAdminService.getEmployees}
        dataState={state.employees.dataState}
        onDataReceived={onDataReceived}
        bypassRequestDataIfNeeded={state.bypassRequestDataIfNeeded}
      />
      <div>
        <Loader loading={!state.employees.data} loader={<LoadingSpinner />}>
          <div className="admin-grid employees-grid">
            <Grid
              data={state.employees.data}
              pageable={{
                pageSizes: config.grid.pageSizes,
                pageSizeValue: state.employees.dataState.take,
                info: false,
                previousNext: true,
              }}
              {...state.employees.dataState}
              onDataStateChange={e => onDataStateChange(e)}
              total={state.employees.total}
              sort={state.employees.dataState.sort ?? []}
              sortable={{ allowUnsort: true, mode: 'single' }}
              columns={[
                {
                  title: translate(resources.admin.employees.grid.columns.name),
                  field: 'fullName',
                  sortable: true,
                  cells: { data: employeeNameCell },
                },
                {
                  title: translate(
                    resources.admin.employees.grid.columns.email,
                  ),
                  field: 'email',
                  sortable: false,
                  cell: emailCell,
                  headerClassName: 'grid-email',
                },
                {
                  title: translate(
                    resources.admin.employees.grid.columns.company,
                  ),
                  field: 'companyName',
                  sortable: false,
                  cell: companyNameCell,
                },
                {
                  title: translate(
                    resources.admin.employees.grid.columns.state,
                  ),
                  field: 'isActive',
                  sortable: true,
                  cell: StateCell,
                },
                {
                  cell: ResendEmailCustomCell,
                  headerClassName: 'grid-actions-cell',
                },
              ]}
            />
          </div>
        </Loader>
      </div>
    </div>
  );
}
